import { useMemo } from "react"
import Circle from "@mui/icons-material/Circle"
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material"
import { PieChart } from "@mui/x-charts"

import type { PiePlotView, RecordType } from "types/dashboard.types"

import usePieConfig from "widgets/plots/configuration/usePieConfig"
import MuiPieTooltip from "./MuiPieTooltip"
import TooltipPositionBridge from "./TooltipPositionBridge"

interface MuiPieChartProps {
  view: PiePlotView
  data: RecordType[]
  showLegend?: boolean
}

const MuiPieChart = ({ view, data, showLegend = true }: MuiPieChartProps) => {
  const theme = useTheme()
  const isDownMd = useMediaQuery(theme.breakpoints.down("md"))
  const isDownLg = useMediaQuery(theme.breakpoints.down("lg"))
  const config = usePieConfig(view, data)
  const { filter, plotSize, plotData, filteredPlotData } = config

  const size = useMemo(
    () => (isDownMd ? plotSize.sm : isDownLg ? plotSize.md : plotSize.lg),
    [isDownMd, isDownLg, plotSize],
  )

  const circleIcon = size / 28

  const { prefix, suffix } = view.properties

  const series = useMemo(
    () => [
      {
        data: filteredPlotData.map((d) => ({
          id: d.name,
          value: d.value,
          label: d.name,
          color: d.color,
        })),
        valueFormatter: (v: { value: number }) =>
          `${prefix ?? ""}${v.value}${suffix ?? ""}`,
        innerRadius: size / 4.5,
        outerRadius: size / 2.75 + size / 13,
        arcLabel: "value" as const,
        arcLabelMinAngle: 20,
        highlightScope: { highlight: "item", fade: "global" } as const,
        highlighted: { additionalRadius: 1 },
        faded: { additionalRadius: -1 },
      },
    ],
    [filteredPlotData, size, prefix, suffix],
  )

  return (
    <Stack
      direction={isDownLg ? "column" : "row"}
      justifyContent="center"
      alignItems="center"
      gap={2}
      overflow="hidden"
      textAlign="center"
      pb={2}
    >
      <Box sx={{ width: size, height: size, flexShrink: 0 }}>
        <PieChart
          width={size}
          height={size}
          series={series}
          hideLegend
          skipAnimation
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          slots={{ tooltip: MuiPieTooltip }}
          sx={{
            "& svg": { overflow: "visible" },
            "& .MuiPieArc-root": {
              stroke: theme.palette.background.default,
              filter: `drop-shadow(0px 0px 5px ${filter})`,
            },
            "&& .MuiPieArcLabel-root": {
              fill: theme.palette.grey[800],
              fontWeight: "bold",
              fontSize: theme.typography.body1.fontSize,
            },
          }}
        >
          <TooltipPositionBridge />
        </PieChart>
      </Box>
      {showLegend && (
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="flex-start"
          gap={1.5}
          maxWidth={size}
          flexDirection={isDownLg ? "row" : "column"}
        >
          {plotData.map((pd) => (
            <Stack
              key={pd.name}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              sx={{ width: "100%" }}
            >
              <Stack direction="row" alignItems="flex-start" gap={1}>
                <Circle sx={{ color: pd.color, fontSize: circleIcon, mt: "3px" }} />
                <Typography textAlign="left">{pd.name}</Typography>
              </Stack>
              <Typography sx={{ fontVariantNumeric: "tabular-nums" }}>
                {pd.value}
              </Typography>
            </Stack>
          ))}
        </Box>
      )}
    </Stack>
  )
}

export default MuiPieChart
